<template>
  <div class="beck-footer">
    <div class="row justify-content-center">
      <div class="col-xxl-6">
        <ul>
          <li>
            <a class="beck-link" :aria-label="imprintText" :href="imprintLink">
              {{ imprintText }}
            </a>
          </li>
          <li>
            <a class="beck-link" :aria-label="agbText" :href="agbLink">
              {{ agbText }}
            </a>
          </li>
          <li>
            <a class="beck-link" :aria-label="dataProtectionText" :href="dataProtectionLink">
              {{ dataProtectionText }}
            </a>
          </li>
          <li>
            <a class="beck-link" :aria-label="dataProtectionSettingsText" :href="dataProtectionSettingsLink">
              {{ dataProtectionSettingsText }}
            </a>
          </li>
          <li>
            <a class="beck-link" :aria-label="careerText" :href="careerLink">
              {{ careerText }}
            </a>
          </li>
        </ul>
      </div>
      <div class="col-xxl-2 socials-col">
        <ul class="socials">
          <li>
            <a
              :aria-label="socialMediaFacebook"
              href="https://www.facebook.com/VerlagCHBeck/"
              :title="socialMediaFacebook">
              <i class="fa-brands fa-square-facebook"></i>
            </a>
          </li>
          <li>
            <a
              :aria-label="socialMediaLinkedIn"
              href="https://de.linkedin.com/company/verlag-c-h-beck"
              :title="socialMediaLinkedIn">
              <i class="fa-brands fa-linkedin"></i>
            </a>
          </li>
          <li>
            <a :aria-label="socialMediaRss" href="/Rss/Fachnews" :title="socialMediaRss">
              <i class="fa-solid fa-square-rss"></i>
            </a>
          </li>
          <li v-if="development">
            <a aria-label="DevMode" :href="debugRoute">
              <i class="fa-solid fa-wand-magic-sparkles"></i>
            </a>
          </li>
        </ul>
      </div>
      <div class="col-xxl-1 copyright-col" v-if="showCopyright">
        <ul>
          <li>
            <a class="beck-link" :href="copyrightLink">&copy;&nbsp;{{ currentYear }}</a>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, Ref } from "vue";

const currentYear: string = new Date().getFullYear().toString();
const development: Ref<boolean> = ref(false);
const debugRoute: Ref<string> = ref("");
debugRoute.value = window.location.origin + "/debug";
development.value = window.location.origin.includes("localhost") ? true : false;

defineProps({
  showCopyright: { type: Boolean, default: true },
  copyrightLink: { type: String, default: "/impressum#urhg" },
  imprintText: { type: String, default: "Impressum" },
  imprintLink: { type: String, default: "/impressum" },
  agbText: { type: String, default: "AGB" },
  agbLink: { type: String, default: "https://rsw.beck.de/beck-online-service/agbs-beck-online" },
  dataProtectionText: { type: String, default: "Datenschutz" },
  dataProtectionLink: { type: String, default: "https://rsw.beck.de/beck-online-service/datenschutz" },
  dataProtectionSettingsText: { type: String, default: "Datenschutz-Einstellungen" },
  dataProtectionSettingsLink: { type: String, default: "/Home?openUc" },
  careerText: { type: String, default: "Karriere" },
  careerLink: { type: String, default: "https://karriere.chbeck.de/" },
  socialMediaFacebook: { type: String, default: "beck-online auf Facebook" },
  socialMediaLinkedIn: { type: String, default: "beck-online auf LinkedIn" },
  socialMediaRss: { type: String, default: "beck-online Fachnews Rss-Feed" }
});
</script>

<style scoped lang="scss">
.beck-footer {
  position: fixed;
  left: 0;
  bottom: 0;
  width: 100%;
  text-align: center;

  ul {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 0;
    li {
      display: inline;
      padding: 0.5rem 0rem 0.5rem 1rem;
      a {
        text-decoration: underline;
        border-right: none;
        padding-right: 1rem;
        font-size: 0.95rem;
        &:hover {
          text-decoration: none;
        }
        @media (max-width: 1550px) {
          font-size: 0.83rem;
        }
      }
      i,
      svg {
        height: 1.25rem;
      }
      @media (max-width: 600px) {
        padding: 0.5rem 0rem 0.5rem 0.5rem;
        i,
        svg {
          height: 1.2rem;
        }
      }
    }

    @media (max-width: 600px) {
      padding-left: 0rem;
    }
  }

  .socials-col {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 0;
  }

  .copyright-col {
    max-width: fit-content;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 0;
  }
}

@media only screen and (max-width: 900px) {
  .beck-footer {
    position: relative;
    width: 100%;
    ul {
      display: block;
      li {
        display: block;
      }
      li:not(:last-child) {
        a {
          border-right: none;
        }
      }
    }
    ul.socials {
      display: flex;
      padding-left: 0;
      li {
        display: inline-block;
        padding-right: 1rem;
        a {
          padding-right: 1rem;
          padding-left: 1rem;
        }
        svg,
        i {
          height: 1.5rem;
        }
      }
    }
    .row {
      margin-right: 0;
      margin-left: 0;
    }
    .socials-col {
      padding-right: 0;
      padding-left: 0;
      padding-top: 1.5rem;
      padding-bottom: 1.4rem;
    }
  }
}

@media (prefers-color-scheme: light) {
  :root:not(.beck-light):not(.beck-dark).beck-auto-scheme .beck-footer {
    background-color: var(--beck-gray-10);
    color: var(--beck-gray-70);
    ul li a {
      color: var(--beck-gray-70);
    }
  }
}

@media (prefers-color-scheme: dark) {
  :root:not(.beck-light):not(.beck-dark).beck-auto-scheme .beck-footer {
    background-color: #000000;
    color: var(--beck-gray-30);
    ul li a {
      color: var(--beck-gray-30);
    }
  }
}

.beck-light .beck-footer,
:root:not(.beck-light):not(.beck-dark):not(.beck-auto-scheme) .beck-footer {
  background-color: var(--beck-gray-10);
  color: var(--beck-gray-70);
  ul li a {
    color: var(--beck-gray-70);
  }
}

.beck-dark .beck-footer {
  background-color: #000000;
  color: var(--beck-gray-30);
  ul li a {
    color: var(--beck-gray-30);
  }
}
</style>
